import { cn } from '@workspace/ui/lib';
import * as React from 'react';

// import { useTranslations } from "next-intl";

const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<'textarea'> & {
    description?: string;
    maxWords?: number;
    readOnly?: boolean;
    /** Masks the textarea value in PostHog session recordings for sensitive data. */
    sensitive?: boolean;
  }
>(function Textarea(
  {
    className,
    description,
    maxWords,
    value,
    onChange,
    placeholder,
    readOnly,
    sensitive = false,
    ...props
  },
  ref
) {
  // const t = useTranslations("form");

  const text = typeof value === 'string' ? value : '';

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const incoming = e.target.value;
      const pieces = incoming.trim() ? incoming.trim().split(/\s+/) : [];
      const next =
        pieces.length > (maxWords ?? 100)
          ? `${pieces.slice(0, maxWords ?? 100).join(' ')} `
          : incoming;

      if (next !== incoming) {
        e.persist();
        Object.assign(e.target, { value: next });
      }
      onChange?.(e);
    },
    [maxWords, onChange]
  );

  const showFooter = !readOnly && (description || maxWords);

  return (
    <div className={cn(showFooter && 'space-y-2')}>
      <textarea
        ref={ref}
        data-slot="textarea"
        className={cn(
          'caret-primary dark:caret-neutral-100 placeholder:text-grey-700 dark:placeholder:text-grey-500 selection:bg-grey-100 aria-invalid:border-destructive dark:bg-input/30 [&:not(:placeholder-shown):not(:disabled)]:text-primary disabled:border-grey-100 dark:disabled:border-grey-900 disabled:text-grey-300 disabled:placeholder:text-grey-300 dark:disabled:text-grey-900 dark:disabled:placeholder:text-grey-900 flex min-h-32 w-full max-w-full resize-none rounded-2xl bg-transparent px-4 py-3 text-base leading-5 transition-[color,box-shadow] outline-none disabled:cursor-not-allowed dark:aria-invalid:border-red-400 dark:[&:not(:placeholder-shown):not(:disabled)]:text-neutral-100',
          readOnly
            ? 'border-grey-300 dark:border-grey-600 text-grey-600! mb-0 min-h-12 cursor-default border'
            : 'border-grey-300 hover:border-primary dark:border-grey-700 focus-visible:border-primary border shadow-xs',
          sensitive && 'ph-no-capture',
          className
        )}
        placeholder={placeholder ?? 'Type here'}
        readOnly={readOnly}
        {...props}
        {...(value !== undefined ? { value: text } : { defaultValue: props.defaultValue || '' })}
        onChange={handleChange}
      />
      <div className="flex items-center justify-between">
        {description && !readOnly && (
          <p
            className={cn(
              'text-grey-500 text-sm',
              props.disabled && 'text-grey-300 dark:text-grey-900'
            )}
          >
            {description}
          </p>
        )}
        {maxWords && !readOnly && (
          <span
            className={cn(
              'text-grey-500 text-sm',
              props.disabled && 'text-grey-300 dark:text-grey-900'
            )}
          >
            {maxWords} words max
          </span>
        )}
      </div>
    </div>
  );
});

Textarea.displayName = 'Textarea';

export { Textarea };
